import type { TableMobileRowProps } from "../../../Table";
import type { TradingClientRow, TradingColumnLabels } from "../../types";

import { TableMobileHeader, TableMobileRow } from "../../../Table";
import { formatCurrencyWithSymbol } from "../../../utils/currency";
import { formatNumberV2 } from "../../utils";
import { MobileRebateProgramValue } from "./MobileRebateProgramValue";

export type Props = {
  language: string;
  translatedContent: TradingColumnLabels;
  columnVisibility: Record<string, boolean>;
  showOfferIdColumn?: boolean;
  onSharedProgramClick?: (hasOfferID: string) => void;
};

export const MobileCardBody =
  ({
    language,
    translatedContent,
    showOfferIdColumn = false,
    onSharedProgramClick,
  }: Props) =>
  (row: TradingClientRow) => {
    const {
      tradingAccountID,
      tradingAccountType,
      volumeFX,
      volumeXAU,
      volumeXAG,
      volumeOil,
      volumeCFD,
      hasOfferID,
      subIBs,
      rebateAccountID,
    } = row;

    return (
      <>
        <TableMobileRow
          label={`${translatedContent?.acc}`}
          value={tradingAccountID}
        />
        <TableMobileRow
          label={translatedContent.accType}
          value={tradingAccountType}
        />
        <TableMobileRow
          label={translatedContent.colVolFx}
          value={formatNumberV2(volumeFX, language)}
        />
        <TableMobileRow
          label={translatedContent.colVolXau}
          value={formatNumberV2(volumeXAU, language)}
        />
        <TableMobileRow
          label={translatedContent.colVolXag}
          value={formatNumberV2(volumeXAG, language)}
        />
        <TableMobileRow
          label={translatedContent.colVolOil}
          value={formatNumberV2(volumeOil, language)}
        />
        <TableMobileRow
          label={translatedContent.colVolCfd}
          value={formatNumberV2(volumeCFD, language)}
        />
        {showOfferIdColumn && (
          <TableMobileRow
            label={translatedContent.hasOfferID}
            valueClassName="min-w-0"
            value={
              <MobileRebateProgramValue
                hasOfferID={hasOfferID}
                subIBs={subIBs}
                onSharedProgramClick={onSharedProgramClick}
              />
            }
          />
        )}
        <TableMobileRow
          label={translatedContent.rebateAccount}
          value={rebateAccountID}
        />
      </>
    );
  };

export const MobileCardHeader =
  ({ language, translatedContent, columnVisibility }: Props) =>
  (row: TradingClientRow) => {
    const {
      fullName,
      email,
      totalEarnings,
      currency,
      clientId,
      totalVolumeUSD,
    } = row;

    const clientIdConfig = !Object.keys(columnVisibility).includes("clientId")
      ? {
          value: clientId,
          label: translatedContent.clientId,
        }
      : undefined;
    const subHeaderRows: TableMobileRowProps[] = [];

    subHeaderRows.push({
      label: translatedContent.totalVolume,
      value: formatCurrencyWithSymbol(
        totalVolumeUSD ?? 0,
        "USD",
        language,
        false,
        false,
      ),
    });

    subHeaderRows.push({
      label: translatedContent.totalEarnings,
      value: formatCurrencyWithSymbol(
        totalEarnings,
        currency,
        language,
        false,
        false,
      ),
    });

    return (
      <TableMobileHeader
        name={fullName}
        email={email}
        subHeaderRows={subHeaderRows}
        clientId={clientIdConfig}
      />
    );
  };
